import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { useAppContext } from "../context/AppContext";
import {
  SHIPPING_CHARGE,
  FREE_SHIPPING_THRESHOLD,
  STATE_NAME,
  COUNTRY_NAME,
  createEmptyAddress,
  checkDeliveryAvailabilityLocal,
  getSelectedDeliveryLocation,
} from "../utils/deliveryService";
import { lookupPincode, INDIAN_STATES } from "../utils/pincodeService";
import FloatingWidgets from "./components/FloatingWidgets";

const normalizeImage = (path) => {
  if (!path) return "";
  if (typeof path !== "string") return "";

  const cleanPath = path.trim();
  if (cleanPath.startsWith("http://") || cleanPath.startsWith("https://")) {
    return cleanPath;
  }
  if (cleanPath.startsWith("//")) {
    return `https:${cleanPath}`;
  }
  if (
    cleanPath.startsWith("dsdxwhpuu/") ||
    cleanPath.startsWith("image/upload/") ||
    cleanPath.includes("cloudinary.com")
  ) {
    const withoutHost = cleanPath.replace(/^https?:\/\/res\.cloudinary\.com\/?/, "").replace(/^\/+/, "");
    return `https://res.cloudinary.com/${withoutHost}`;
  }
  if (cleanPath.startsWith("assets/") || cleanPath.startsWith("/assets/")) {
    return cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  }

  const normalized = cleanPath.replace(/\\/g, "/").replace(/^\/+/, "");
  return `${API_BASE_URL}/${normalized}`;
};

export default function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cartItems, user, removeCartItem, clearCart } = useAppContext();

  const [loading, setLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState({ email: "" });
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);

  const [newAddress, setNewAddress] = useState(createEmptyAddress());
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [billingAddress, setBillingAddress] = useState(createEmptyAddress());

  // Pincode Lookup states
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [billingPincodeLoading, setBillingPincodeLoading] = useState(false);
  const [suggestedLocalities, setSuggestedLocalities] = useState([]);
  const [pincodeStatus, setPincodeStatus] = useState({ message: "", isSuccess: false });
  const [billingPincodeStatus, setBillingPincodeStatus] = useState({ message: "", isSuccess: false });

  const [errors, setErrors] = useState({});
  const [billingErrors, setBillingErrors] = useState({});
  const [newsletter, setNewsletter] = useState(false);

  // Coupon States
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [shippingAvailability, setShippingAvailability] = useState({
    checking: false,
    checked: false,
    available: true,
    message: "",
    shippingCharge: 0,
    matchedLocation: null,
  });

  useEffect(() => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (!token && cartItems.length === 0) {
      navigate("/categories");
    } else if (token && cartItems.length === 0) {
      const timer = setTimeout(() => {
        if (cartItems.length === 0) navigate("/categories");
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [cartItems, navigate]);

  useEffect(() => {
    if (user) {
      setContactInfo({ email: user.email || "" });
      const def =
        user.addresses?.find((a) => a.isDefault) || user.addresses?.[0];
      if (def) {
        setSelectedAddress(def);
        setShowNewAddressForm(false);
      } else {
        setShowNewAddressForm(true);
      }
    } else {
      setShowNewAddressForm(true);
    }
  }, [user]);

  useEffect(() => {
    const routeAddress = location.state?.selectedDeliveryLocation;
    const savedAddress = getSelectedDeliveryLocation();
    const incoming = routeAddress || savedAddress;

    if (incoming) {
      setNewAddress((prev) => ({
        ...prev,
        ...incoming,
        country: incoming.country || COUNTRY_NAME,
        state: incoming.state || STATE_NAME,
      }));
      setShowNewAddressForm(true);

      if (incoming.pincode && /^\d{6}$/.test(incoming.pincode)) {
        lookupPincode(incoming.pincode).then((res) => {
          if (res.success && res.localities?.length) {
            setSuggestedLocalities(res.localities);
          }
        });
      }
    }
  }, [location.state]);

  const subtotal = useMemo(
    () => cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0),
    [cartItems],
  );

  const staticCoupon = useMemo(() => {
    const minAmount = 1000;
    if (subtotal < minAmount) return null;
    return {
      code: "A1BAK1025",
      description: "10% Instant Discount",
      value: 10,
      type: "percentage",
      minOrderAmount: minAmount,
    };
  }, [subtotal]);

  const shippingAddressToUse = showNewAddressForm
    ? newAddress
    : selectedAddress;

  useEffect(() => {
    if (!shippingAddressToUse?.pincode) {
      setShippingAvailability({
        checking: false,
        checked: false,
        available: true,
        message: "",
        shippingCharge: subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_CHARGE,
        matchedLocation: null,
      });
      return;
    }

    const result = checkDeliveryAvailabilityLocal(shippingAddressToUse, subtotal);

    setShippingAvailability({
      checking: false,
      checked: true,
      available: result.available,
      message: result.message,
      shippingCharge: result.shippingCharge,
      matchedLocation: result.matchedLocation,
    });
  }, [shippingAddressToUse, subtotal]);

  const shipping = shippingAvailability.available
    ? shippingAvailability.shippingCharge
    : subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_CHARGE;

  const discount = useMemo(() => {
    if (!appliedCoupon) return 0;

    if (appliedCoupon.type === "percentage") {
      return Math.round((subtotal * appliedCoupon.value) / 100);
    }
    return appliedCoupon.value;
  }, [appliedCoupon, subtotal]);

  const total = Math.max(0, subtotal + shipping - discount);

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setErrors((prev) => ({ ...prev, coupon: "" }));
  };

  // Handle PIN Code Auto-Lookup (Amazon/Flipkart style)
  const handlePincodeChange = async (val, isBilling = false) => {
    const cleanPin = val.replace(/\D/g, "").slice(0, 6);

    if (isBilling) {
      updateBillingField("pincode", cleanPin);
      if (cleanPin.length === 6) {
        setBillingPincodeLoading(true);
        setBillingPincodeStatus({ message: "Finding city...", isSuccess: false });
        const res = await lookupPincode(cleanPin);
        setBillingPincodeLoading(false);
        if (res.success) {
          setBillingAddress((prev) => ({
            ...prev,
            city: res.city || prev.city,
            district: res.district || prev.district,
            state: res.state || prev.state,
          }));
          setBillingPincodeStatus({
            message: `✓ ${res.city}, ${res.state}`,
            isSuccess: true,
          });
        } else {
          setBillingPincodeStatus({ message: res.message || "", isSuccess: false });
        }
      } else {
        setBillingPincodeStatus({ message: "", isSuccess: false });
      }
    } else {
      updateAddressField("pincode", cleanPin);
      if (cleanPin.length === 6) {
        setPincodeLoading(true);
        setPincodeStatus({ message: "Finding city...", isSuccess: false });
        const res = await lookupPincode(cleanPin);
        setPincodeLoading(false);
        if (res.success) {
          setNewAddress((prev) => ({
            ...prev,
            city: res.city || prev.city,
            district: res.district || prev.district,
            state: res.state || prev.state,
          }));
          setSuggestedLocalities(res.localities || []);
          setPincodeStatus({
            message: `✓ ${res.city}, ${res.state}`,
            isSuccess: true,
          });
        } else {
          setSuggestedLocalities([]);
          setPincodeStatus({ message: res.message || "", isSuccess: false });
        }
      } else {
        setSuggestedLocalities([]);
        setPincodeStatus({ message: "", isSuccess: false });
      }
    }
  };

  // Quick locality chip selection
  const handleSelectLocality = (locality) => {
    setNewAddress((prev) => ({
      ...prev,
      addressLine2: locality,
    }));
    setErrors((prev) => ({ ...prev, addressLine2: "" }));
  };

  // Proactively track abandonment when email is entered
  const trackAbandonment = async () => {
    if (!contactInfo.email || !/\S+@\S+\.\S+/.test(contactInfo.email)) return;
    if (sessionStorage.getItem("checkout_id")) return;

    try {
      const name =
        `${shippingAddressToUse?.firstName || ""} ${shippingAddressToUse?.lastName || ""}`.trim();
      const { data } = await axios.post(
        `${API_BASE_URL}/api/payment/track-abandonment`,
        {
          email: contactInfo.email,
          customerName: name || "Guest Customer",
          phone: shippingAddressToUse?.phone || "0000000000",
          items: cartItems.map((item) => ({
            productId: item.productId || item._id,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            image: normalizeImage(item.img),
            net_wt: item.net_wt,
          })),
          pricing: {
            total: total || subtotal,
            subtotal,
            couponCode: appliedCoupon?.code || null,
          },
          shippingAddress: shippingAddressToUse || {},
        },
      );
      if (data.internalOrderId) {
        sessionStorage.setItem("checkout_id", data.internalOrderId);
      }
    } catch (e) {
      console.warn("Tracking failed gracefully:", e?.message);
    }
  };

  const handleApplyCoupon = async (codeToApply) => {
    const code = (typeof codeToApply === "string" ? codeToApply : couponCode).trim().toUpperCase();
    if (!code) return;

    if (!contactInfo.email || !shippingAddressToUse?.phone) {
      setErrors((prev) => ({
        ...prev,
        coupon: "Please enter your email and phone number first.",
      }));
      return;
    }

    setCouponLoading(true);
    setErrors((prev) => ({ ...prev, coupon: "" }));

    if (staticCoupon && code === staticCoupon.code) {
      setAppliedCoupon(staticCoupon);
      setCouponCode("");
      setCouponLoading(false);
      return;
    }

    try {
      const userPhone = shippingAddressToUse?.phone || "";
      const userEmail = contactInfo.email || "";

      const { data } = await axios.post(
        `${API_BASE_URL}/api/coupons/validate`,
        {
          code: code,
          email: userEmail.toLowerCase().trim(),
          phone: userPhone.replace(/\D/g, "").slice(-10),
          cartTotal: Math.round(subtotal),
        },
      );
      if (data.success) {
        setAppliedCoupon(data.coupon);
        setCouponCode("");
      } else {
        setErrors((prev) => ({ ...prev, coupon: data.message }));
      }
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        coupon: err.response?.data?.message || "Failed to validate coupon.",
      }));
    } finally {
      setCouponLoading(false);
    }
  };

  const updateAddressField = (field, value) => {
    setNewAddress((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
      delivery: "",
    }));
  };

  const updateBillingField = (field, value) => {
    setBillingAddress((prev) => ({
      ...prev,
      [field]: value,
    }));

    setBillingErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const validateShippingAddress = () => {
    const nextErrors = {};
    const current = shippingAddressToUse || {};

    if (!contactInfo.email?.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(contactInfo.email)) {
      nextErrors.email = "Please enter a valid email address.";
    }

    if (!current.firstName?.trim()) {
      nextErrors.firstName = "First name is required.";
    }

    if (!current?.phone?.trim()) {
      nextErrors.phone = "Mobile number is required.";
    } else if (!/^\d{10}$/.test(String(current.phone).replace(/\D/g, ""))) {
      nextErrors.phone = "Enter a valid 10-digit mobile number.";
    }

    if (!/^\d{6}$/.test(String(current.pincode || "").replace(/\D/g, ""))) {
      nextErrors.pincode = "Enter a valid 6-digit PIN code.";
    }

    if (!current.addressLine1?.trim()) {
      nextErrors.addressLine1 = "Flat / House / Building details are required.";
    }

    if (!current.city?.trim()) {
      nextErrors.city = "City / Town is required.";
    }

    if (!current.state?.trim()) {
      nextErrors.state = "State is required.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateBillingAddress = () => {
    if (billingSameAsShipping) return true;

    const nextErrors = {};

    if (!billingAddress.firstName?.trim())
      nextErrors.firstName = "First name is required.";

    if (!billingAddress.phone?.trim()) {
      nextErrors.phone = "Mobile number is required.";
    } else if (!/^\d{10}$/.test(String(billingAddress.phone).replace(/\D/g, ""))) {
      nextErrors.phone = "Enter a valid 10-digit mobile number.";
    }

    if (!/^\d{6}$/.test(String(billingAddress.pincode || "").replace(/\D/g, ""))) {
      nextErrors.pincode = "Enter a valid 6-digit PIN code.";
    }

    if (!billingAddress.addressLine1?.trim())
      nextErrors.addressLine1 = "Flat / House / Building is required.";

    if (!billingAddress.city?.trim())
      nextErrors.city = "City / Town is required.";

    if (!billingAddress.state?.trim())
      nextErrors.state = "State is required.";

    setBillingErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePlaceOrder = async () => {
    const validShipping = validateShippingAddress();
    const validBilling = validateBillingAddress();

    if (!validShipping || !validBilling) {
      setTimeout(() => {
        const firstError = document.querySelector(
          ".is-invalid, .error-text, .delivery-alert.danger",
        );
        if (firstError) {
          const yOffset = -140;
          const y =
            firstError.getBoundingClientRect().top +
            window.pageYOffset +
            yOffset;

          window.scrollTo({ top: y, behavior: "smooth" });

          if (
            firstError.tagName === "INPUT" ||
            firstError.tagName === "SELECT"
          ) {
            firstError.focus();
          } else {
            const input = firstError
              .closest(".field-group, .col-md-6, .col-md-4, .col-12")
              ?.querySelector("input, select");
            if (input) input.focus();
          }
        }
      }, 100);
      return;
    }

    try {
      setLoading(true);

      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded) {
        alert("Unable to load payment gateway. Please check your internet connection.");
        return;
      }

      const isFirstAttempt = !sessionStorage.getItem(
        "checkout_abandon_tracked",
      );

      const formattedFullAddress = [
        shippingAddressToUse.firstName && `${shippingAddressToUse.firstName} ${shippingAddressToUse.lastName || ""}`.trim(),
        shippingAddressToUse.addressLine1,
        shippingAddressToUse.addressLine2,
        shippingAddressToUse.landmark && `Near ${shippingAddressToUse.landmark}`,
        `${shippingAddressToUse.city}${shippingAddressToUse.district ? ` (${shippingAddressToUse.district})` : ""}`,
        `${shippingAddressToUse.state} - ${shippingAddressToUse.pincode}`,
      ].filter(Boolean).join(", ");

      const orderPayload = {
        customer: {
          email: contactInfo.email,
          userId: user?.id || user?._id || null,
        },
        shippingAddress: {
          ...shippingAddressToUse,
          formattedAddress: formattedFullAddress,
        },
        billingAddress: billingSameAsShipping
          ? { ...shippingAddressToUse, formattedAddress: formattedFullAddress }
          : billingAddress,
        billingSameAsShipping,
        items: cartItems.map((item) => ({
          productId: item.productId || item._id || item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          image: normalizeImage(item.img),
          net_wt: item.net_wt,
        })),
        pricing: {
          subtotal,
          shipping,
          discount,
          couponCode: appliedCoupon?.code,
          total,
        },
        newsletter,
        isCheckoutStarted: isFirstAttempt,
        internalOrderId: sessionStorage.getItem("checkout_id"),
      };

      const { data } = await axios.post(
        `${API_BASE_URL}/api/payment/create-order`,
        orderPayload,
        {
          headers: {
            "x-auth-token":
              localStorage.getItem("token") || sessionStorage.getItem("token"),
          },
        },
      );

      sessionStorage.setItem("checkout_abandon_tracked", "true");

      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: data.currency || "INR",
        name: "A1 Bakery & Sweets",
        description: "Fresh Sweets & Savouries Order",
        order_id: data.razorpayOrderId,
        image: "https://staging.a1bakeryandsweets.com/assets/img/a1-img/Logo.png",
        prefill: {
          name: `${shippingAddressToUse.firstName || ""} ${shippingAddressToUse.lastName || ""}`.trim(),
          email: contactInfo.email,
          contact: shippingAddressToUse.phone?.replace(/\D/g, "").slice(-10) || "",
        },
        notes: {
          shipping_address: formattedFullAddress,
        },
        theme: {
          color: "#991b1b",
        },
        config: {
          display: {
            payment_methods: {
              upi: true,
              cards: true,
              netbanking: true,
              wallets: true,
            },
          },
        },
        handler: async function (response) {
          try {
            const token =
              localStorage.getItem("token") || sessionStorage.getItem("token");
            const headers = token ? { "x-auth-token": token } : {};

            const finalOrderPayload = {
              customerName: `${shippingAddressToUse.firstName || ""} ${shippingAddressToUse.lastName || ""}`.trim() || "Customer",
              email: contactInfo.email,
              phone:
                shippingAddressToUse.phone?.replace(/\D/g, "").slice(-10) ||
                "",
              shippingAddress: {
                addressLine1: shippingAddressToUse.addressLine1 || "",
                addressLine2: shippingAddressToUse.addressLine2 || "",
                city: shippingAddressToUse.city || "",
                district: shippingAddressToUse.district || shippingAddressToUse.city || "",
                state: shippingAddressToUse.state || STATE_NAME,
                pincode: shippingAddressToUse.pincode || "",
                country: shippingAddressToUse.country || "India",
              },
              items: orderPayload.items,
              subtotal: orderPayload.pricing.subtotal,
              shippingCharge: orderPayload.pricing.shipping,
              tax: orderPayload.pricing.tax || 0,
              totalAmount: orderPayload.pricing.total,
              couponCode: appliedCoupon?.code || null,
              userId: user?._id || user?.id || null,
            };

            const verifyRes = await axios.post(
              `${API_BASE_URL}/api/payment/verify-razorpay-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                appOrderId: data.appOrderId,
                orderDetails: finalOrderPayload,
              },
              { headers },
            );

            if (verifyRes.data?.success) {
              const confirmedOrderId =
                verifyRes.data.orderId ||
                verifyRes.data.appOrderId ||
                data.appOrderId;

              if (typeof clearCart === "function") {
                clearCart();
              } else {
                localStorage.removeItem("cartItems");
              }
              sessionStorage.removeItem("checkout_abandon_tracked");
              sessionStorage.removeItem("checkout_id");
              navigate(`/order-success/${confirmedOrderId}`);
            } else {
              alert("Payment verification failed. Please contact support if amount was deducted.");
            }
          } catch (err) {
            console.error("Order completion error:", err);
            if (data.appOrderId) {
              if (typeof clearCart === "function") {
                clearCart();
              } else {
                localStorage.removeItem("cartItems");
              }
              sessionStorage.removeItem("checkout_abandon_tracked");
              sessionStorage.removeItem("checkout_id");
              navigate(`/order-success/${data.appOrderId}`);
            } else {
              alert("Payment completed! Redirecting to confirmation...");
              navigate("/categories");
            }
          }
        },
      };

      const rzpay = new window.Razorpay(options);
      rzpay.open();
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.message || "Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderAddressForm = ({
    address,
    setField,
    fieldErrors,
    type = "shipping",
  }) => {
    const isShipping = type === "shipping";
    const statusObj = isShipping ? pincodeStatus : billingPincodeStatus;
    const isLoading = isShipping ? pincodeLoading : billingPincodeLoading;

    return (
      <div className="advanced-address-form">
        {/* Name Fields */}
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="field-label">
              <span>First name <span className="req-star">*</span></span>
            </label>
            <input
              type="text"
              className={`pro-form-control ${fieldErrors.firstName ? "is-invalid" : ""}`}
              placeholder="First name"
              value={address.firstName || ""}
              onChange={(e) => setField("firstName", e.target.value)}
            />
            {fieldErrors.firstName && (
              <small className="error-text">{fieldErrors.firstName}</small>
            )}
          </div>

          <div className="col-md-6">
            <label className="field-label">
              <span>Last name <span className="text-muted fw-normal" style={{ fontSize: "12px" }}>(optional)</span></span>
            </label>
            <input
              type="text"
              className="pro-form-control"
              placeholder="Last name"
              value={address.lastName || ""}
              onChange={(e) => setField("lastName", e.target.value)}
            />
          </div>
        </div>

        {/* Mobile Phone & PIN Code */}
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="field-label">
              <span>Mobile number <span className="req-star">*</span></span>
            </label>
            <div className={`phone-input-group ${fieldErrors.phone ? "is-invalid" : ""}`}>
              <div className="phone-input-prefix">
                <span>+91</span>
              </div>
              <input
                type="tel"
                className="phone-input-field"
                placeholder="10-digit mobile number"
                maxLength={10}
                value={address.phone || ""}
                onChange={(e) => setField("phone", e.target.value.replace(/\D/g, ""))}
              />
            </div>
            {fieldErrors.phone && (
              <small className="error-text">{fieldErrors.phone}</small>
            )}
          </div>

          {/* 6-Digit PIN Code with Auto-Discovery */}
          <div className="col-md-6">
            <label className="field-label">
              <span>
                PIN Code <span className="req-star">*</span>
              </span>
              {isLoading && (
                <span className="text-danger small fw-semibold" style={{ fontSize: "12px" }}>
                  <i className="fa-solid fa-spinner fa-spin me-1"></i> Auto-detecting...
                </span>
              )}
            </label>
            <div className="position-relative">
              <input
                type="text"
                className={`pro-form-control ${fieldErrors.pincode ? "is-invalid" : ""}`}
                placeholder="e.g. 625001"
                maxLength={6}
                value={address.pincode || ""}
                onChange={(e) => handlePincodeChange(e.target.value, !isShipping)}
              />
              <span className="location-icon">
                <i className="fa-solid fa-location-dot"></i>
              </span>
            </div>
            {fieldErrors.pincode && (
              <small className="error-text">{fieldErrors.pincode}</small>
            )}
            {statusObj.message && (
              <div>
                <span className={`pincode-pill-badge ${statusObj.isSuccess ? "success" : "muted"}`}>
                  <i className={`fa-solid ${statusObj.isSuccess ? "fa-circle-check" : "fa-circle-info"}`}></i>
                  {statusObj.message}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Suggested Localities Quick Chips (Amazon/Flipkart Style) */}
        {isShipping && suggestedLocalities.length > 0 && (
          <div className="locality-select-card">
            <div className="locality-select-header">
              <i className="fa-solid fa-map-pin text-danger"></i>
              <span>Select Locality / Area to auto-fill street:</span>
            </div>
            <div className="locality-chips-list">
              {suggestedLocalities.map((loc, idx) => {
                const isActive = address.addressLine2 === loc;
                return (
                  <button
                    type="button"
                    key={idx}
                    className={`locality-chip-btn ${isActive ? "active" : ""}`}
                    onClick={() => handleSelectLocality(loc)}
                  >
                    {isActive ? <i className="fa-solid fa-check text-danger"></i> : <i className="fa-solid fa-location-dot text-muted" style={{ fontSize: "10px" }}></i>}
                    {loc}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Flat, House no., Building */}
        <div className="field-group mb-3">
          <label className="field-label">
            <span>Flat, House no., Building, Apartment <span className="req-star">*</span></span>
          </label>
          <input
            type="text"
            className={`pro-form-control ${fieldErrors.addressLine1 ? "is-invalid" : ""}`}
            placeholder="e.g. Flat 302, Sri Krishna Apts"
            value={address.addressLine1 || ""}
            onChange={(e) => setField("addressLine1", e.target.value)}
          />
          {fieldErrors.addressLine1 && (
            <small className="error-text">{fieldErrors.addressLine1}</small>
          )}
        </div>

        {/* Area, Street, Sector */}
        <div className="field-group mb-3">
          <label className="field-label">
            <span>Area, Street, Sector, Village</span>
          </label>
          <input
            type="text"
            className="pro-form-control"
            placeholder="e.g. 4th Cross Street, Tallakulam"
            value={address.addressLine2 || ""}
            onChange={(e) => setField("addressLine2", e.target.value)}
          />
        </div>

        {/* Landmark (Optional) */}
        <div className="field-group mb-3">
          <label className="field-label">
            <span>Landmark <span className="text-muted fw-normal" style={{ fontSize: "12px" }}>(optional)</span></span>
          </label>
          <input
            type="text"
            className="pro-form-control"
            placeholder="e.g. Near Meenakshi Amman Temple / Opp. Post Office"
            value={address.landmark || ""}
            onChange={(e) => setField("landmark", e.target.value)}
          />
        </div>

        {/* City & State */}
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="field-label">
              <span>Town / City <span className="req-star">*</span></span>
            </label>
            <input
              type="text"
              className={`pro-form-control ${fieldErrors.city ? "is-invalid" : ""}`}
              placeholder="e.g. Madurai"
              value={address.city || ""}
              onChange={(e) => setField("city", e.target.value)}
            />
            {fieldErrors.city && (
              <small className="error-text">{fieldErrors.city}</small>
            )}
          </div>

          <div className="col-md-6">
            <label className="field-label">
              <span>State <span className="req-star">*</span></span>
            </label>
            <select
              className="pro-form-control"
              value={address.state || "Tamil Nadu"}
              onChange={(e) => setField("state", e.target.value)}
            >
              {INDIAN_STATES.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };

  if (loading && cartItems.length === 0) {
    return (
      <div className="text-center py-5 mt-100">
        <div className="spinner-border text-danger"></div>
      </div>
    );
  }

  const freeShippingDifference = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);

  return (
    <>
      <Navbar />
      <main className="checkout-page pt-60 pb-100 bg-light">
        <div className="container">
          <div className="breadcrumb__list mb-30">
            <span>
              <Link to="/">Home</Link>
            </span>
            <span className="dvdr">
              <i className="fa-regular fa-angle-right"></i>
            </span>
            <span>Checkout</span>
          </div>

          {/* Stepper Header */}
          <div className="row justify-content-center mb-40">
            <div className="col-lg-8">
              <div className="checkout-steps d-flex justify-content-between position-relative">
                <div className="step-item active">
                  <div className="step-icon">
                    <i className="fa-solid fa-truck-fast"></i>
                  </div>
                  <span className="step-text">Delivery</span>
                </div>
                <div className="step-item active">
                  <div className="step-icon">
                    <i className="fa-solid fa-credit-card"></i>
                  </div>
                  <span className="step-text">Payment</span>
                </div>
                <div className="step-item">
                  <div className="step-icon">
                    <i className="fa-solid fa-circle-check"></i>
                  </div>
                  <span className="step-text">Confirmation</span>
                </div>
                <div className="step-connector"></div>
              </div>
            </div>
          </div>

          <div className="row g-4 px-lg-4 px-2 mt-2">
            <div className="col-lg-8 order-lg-1">
              {/* Free Shipping Alert Bar */}
              {subtotal >= FREE_SHIPPING_THRESHOLD ? (
                <div className="alert alert-success d-flex align-items-center rounded-4 shadow-sm border-0 mb-4 p-3">
                  <i className="fa-solid fa-circle-check text-success fs-4 me-3"></i>
                  <div>
                    <strong className="d-block text-success">🎉 Free Delivery Unlocked!</strong>
                    <span className="small text-muted">Your order qualifies for complimentary fresh delivery.</span>
                  </div>
                </div>
              ) : (
                <div className="alert alert-warning d-flex align-items-center rounded-4 shadow-sm border-0 mb-4 p-3">
                  <i className="fa-solid fa-truck text-warning fs-4 me-3"></i>
                  <div>
                    <strong className="d-block text-dark">Add Rs. {freeShippingDifference} more for FREE Delivery!</strong>
                    <span className="small text-muted">Standard shipping of Rs. {SHIPPING_CHARGE} applies for orders under Rs. {FREE_SHIPPING_THRESHOLD}.</span>
                  </div>
                </div>
              )}

              {/* Section 1: Account Information */}
              <div className="card border-0 shadow-sm rounded-4 mb-4 checkout-card">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold m-0 d-flex align-items-center">
                      <div className="section-number me-3">1</div>
                      Contact Information
                    </h5>
                    {!user && (
                      <Link
                        to="/login"
                        state={{ from: "/checkout" }}
                        className="small text-primary fw-bold text-decoration-none"
                      >
                        Login for faster checkout
                      </Link>
                    )}
                  </div>

                  {user ? (
                    <div className="bg-light p-3 rounded-3 d-flex align-items-center justify-content-between">
                      <div>
                        <p className="m-0 fw-bold">{user.name}</p>
                        <p className="m-0 text-muted small">{user.email}</p>
                      </div>
                      <span className="badge bg-success bg-opacity-10 text-success">Logged In</span>
                    </div>
                  ) : (
                    <div>
                      <label className="field-label">Email address <span className="text-danger">*</span></label>
                      <input
                        type="email"
                        className={`pro-form-control ${errors.email ? "is-invalid" : ""}`}
                        placeholder="yourname@example.com (for order receipt)"
                        value={contactInfo.email}
                        onChange={(e) => {
                          setContactInfo({ email: e.target.value });
                          setErrors((prev) => ({ ...prev, email: "" }));
                        }}
                        onBlur={trackAbandonment}
                      />
                      {errors.email && (
                        <small className="error-text mt-1 d-block">
                          {errors.email}
                        </small>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Section 2: Delivery Address */}
              <div className="card border-0 shadow-sm rounded-4 mb-4 checkout-card">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold m-0 d-flex align-items-center">
                      <div className="section-number me-3">2</div>
                      Delivery Address
                    </h5>
                    {user?.addresses?.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setShowNewAddressForm(!showNewAddressForm)}
                      >
                        {showNewAddressForm ? "Select Saved Address" : "+ Add New Address"}
                      </button>
                    )}
                  </div>

                  {/* Saved Address Cards for Logged-in Users */}
                  {user?.addresses?.length > 0 && !showNewAddressForm && (
                    <div className="mb-4">
                      {user.addresses.map((addr) => (
                        <div
                          key={addr.id || addr._id}
                          className={`border p-3 rounded-3 mb-3 cursor-pointer transition-all ${
                            selectedAddress?.id === addr.id ||
                            selectedAddress?._id === addr._id
                              ? "border-danger bg-light-danger shadow-sm active-address"
                              : ""
                          }`}
                          onClick={() => setSelectedAddress(addr)}
                          style={{
                            borderWidth:
                              selectedAddress?.id === addr.id ||
                              selectedAddress?._id === addr._id
                                ? "2px"
                                : "1px",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <span className="fw-bold">
                                {addr.firstName} {addr.lastName}
                              </span>
                              {addr.isDefault && (
                                <span className="badge bg-secondary ms-2 small">Default</span>
                              )}
                            </div>
                            {(selectedAddress?.id === addr.id ||
                              selectedAddress?._id === addr._id) ? (
                              <i className="fa-solid fa-circle-check text-danger fs-5"></i>
                            ) : (
                              <i className="fa-regular fa-circle text-muted fs-5"></i>
                            )}
                          </div>

                          <div className="address-display small text-secondary">
                            <div>{addr.addressLine1}</div>
                            {addr.addressLine2 && <div>{addr.addressLine2}</div>}
                            <div>{addr.city}, {addr.state} - <strong>{addr.pincode}</strong></div>
                            {addr.phone && (
                              <div className="text-dark fw-medium mt-1">
                                <i className="fa-solid fa-phone me-1 text-success"></i> +91 {addr.phone}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* New Address Entry Form */}
                  {(showNewAddressForm || !user?.addresses?.length) &&
                    renderAddressForm({
                      address: newAddress,
                      setField: updateAddressField,
                      fieldErrors: errors,
                      type: "shipping",
                    })}

                  {shippingAvailability.checked && shippingAvailability.message && (
                    <div
                      className={`delivery-alert ${shippingAvailability.available ? "success" : "danger"}`}
                    >
                      <i className="fa-solid fa-location-dot me-2"></i>
                      {shippingAvailability.message}
                    </div>
                  )}
                </div>
              </div>

              {/* Section 3: Delivery Options */}
              <div className="card border-0 shadow-sm rounded-4 mb-4 checkout-card">
                <div className="card-body p-4">
                  <h5 className="fw-bold mb-3 d-flex align-items-center">
                    <div className="section-number me-3">3</div>
                    Delivery Method
                  </h5>

                  <div className="shipping-box-active">
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold">Fresh Store Delivery / Express Courier</span>
                        {shipping === 0 && (
                          <span className="badge bg-success">FREE</span>
                        )}
                      </div>
                      <p className="m-0 small text-muted">
                        Delivered safely in hygienic, moisture-sealed packaging.
                      </p>
                    </div>
                    <span className="fw-bold fs-6">
                      {shipping === 0 ? "FREE" : `Rs. ${Math.round(Number(shipping))}`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Section 4: Payment */}
              <div className="card border-0 shadow-sm rounded-4 mb-4 checkout-card">
                <div className="card-body p-4">
                  <h5 className="fw-bold mb-1 d-flex align-items-center">
                    <div className="section-number me-3">4</div>
                    Payment Option
                  </h5>
                  <p className="small text-muted mb-3">
                    All transactions are 100% secure, SSL encrypted via Razorpay.
                  </p>

                  <div className="payment-method-card">
                    <div className="payment-method-top">
                      <span className="fw-semibold">
                        Instant UPI (GPay, PhonePe, Paytm), Cards & Netbanking
                      </span>
                      <div className="payment-brand-icons">
                        <img
                          src="/assets/img/a1-img/payment_icons/upi.png"
                          alt="UPI"
                          width="36"
                          height="20"
                        />
                        <img
                          src="/assets/img/a1-img/payment_icons/rupay.png"
                          alt="RuPay"
                        />
                      </div>
                    </div>
                    <div className="payment-method-bottom">
                      You will complete your payment securely with UPI, Credit/Debit Cards, or Netbanking.
                    </div>
                  </div>

                  {/* Billing Address Option */}
                  <div className="billing-title fw-bold mt-4 mb-2">Billing address</div>
                  <div className="billing-selector">
                    <label
                      className={`billing-option ${billingSameAsShipping ? "active" : ""}`}
                    >
                      <input
                        type="radio"
                        name="billingAddressOption"
                        checked={billingSameAsShipping}
                        onChange={() => setBillingSameAsShipping(true)}
                      />
                      <span>Same as delivery address</span>
                    </label>

                    <label
                      className={`billing-option ${!billingSameAsShipping ? "active" : ""}`}
                    >
                      <input
                        type="radio"
                        name="billingAddressOption"
                        checked={!billingSameAsShipping}
                        onChange={() => setBillingSameAsShipping(false)}
                      />
                      <span>Use a different billing address</span>
                    </label>

                    {!billingSameAsShipping && (
                      <div className="billing-form-wrap mt-3">
                        {renderAddressForm({
                          address: billingAddress,
                          setField: updateBillingField,
                          fieldErrors: billingErrors,
                          type: "billing",
                        })}
                      </div>
                    )}
                  </div>

                  <label className="newsletter-check mt-3">
                    <input
                      type="checkbox"
                      checked={newsletter}
                      onChange={(e) => setNewsletter(e.target.checked)}
                    />
                    <span className="small text-muted ms-2">Send me order tracking updates and offers via WhatsApp / Email</span>
                  </label>

                  <button
                    className="bm-hero__cta w-100 mt-4 py-3 fw-bold fs-6"
                    disabled={loading || !shippingAvailability.available}
                    onClick={handlePlaceOrder}
                  >
                    {loading ? (
                      <span>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Processing Order...
                      </span>
                    ) : (
                      `Place Order & Pay Rs. ${Math.round(total)}`
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Right Sidebar: Order Summary & Coupons (Desktop) */}
            <div className="col-lg-4 d-none d-lg-block order-lg-2">
              <div
                className="card border-0 shadow-sm rounded-4 mb-4 sticky-top checkout-summary-card"
                style={{ top: "120px" }}
              >
                <div className="card-body p-4">
                  <h5 className="fw-bold mb-4 d-flex justify-content-between">
                    <span>Order Summary</span>
                    <span className="badge bg-danger rounded-pill fw-normal" style={{ fontSize: "0.75rem" }}>
                      {cartItems.length} {cartItems.length === 1 ? "item" : "items"}
                    </span>
                  </h5>

                  <div className="checkout-items mb-4 mt-2">
                    {cartItems.map((item, idx) => (
                      <div
                        key={idx}
                        className="d-flex gap-3 mb-3 pb-3 border-bottom align-items-center"
                      >
                        <div className="position-relative">
                          <img
                            src={item.img}
                            width="64"
                            height="50"
                            className="rounded-3 border object-fit-cover"
                            alt={item.name}
                          />
                          <span
                            className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-dark border border-white"
                            style={{ fontSize: "0.65rem" }}
                          >
                            {item.quantity}
                          </span>
                        </div>

                        <div className="flex-grow-1">
                          <h6 className="m-0 small fw-bold">{item.name}</h6>
                          <span className="text-muted smaller">
                            Weight: {item.net_wt}
                          </span>
                        </div>

                        <div className="text-end">
                          <span className="small fw-bold">
                            Rs. {Math.round(item.price * item.quantity)}
                          </span>
                        </div>
                        <div className="ms-1">
                          <button
                            className="btn btn-link text-danger p-0 border-0 shadow-none"
                            onClick={() => removeCartItem(idx)}
                            title="Remove item"
                          >
                            <i className="fa-solid fa-trash-can smaller"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Subtotal</span>
                    <span className="fw-bold">
                      Rs. {Math.round(subtotal)}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Shipping</span>
                    <span className={`fw-bold ${shipping === 0 ? "text-success" : ""}`}>
                      {shipping === 0 ? "FREE" : `Rs. ${Math.round(Number(shipping))}`}
                    </span>
                  </div>

                  {discount > 0 && (
                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span>Discount ({appliedCoupon?.code})</span>
                      <span className="fw-bold">- Rs. {discount}</span>
                    </div>
                  )}

                  <hr />

                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold m-0">Total Amount</h5>
                    <h4 className="fw-bold text-danger m-0">
                      Rs. {Math.round(total)}
                    </h4>
                  </div>

                  {/* Coupon Box */}
                  <div className="mt-4 pt-3 border-top">
                    <h6 className="fw-bold mb-3 d-flex align-items-center small">
                      <i className="fa-solid fa-ticket me-2 text-danger"></i>
                      Offers & Coupons
                    </h6>
                    {!appliedCoupon ? (
                      <>
                        {staticCoupon && (
                          <div className="d-flex align-items-center bg-light p-2 rounded-3 border mb-3">
                            <div className="flex-grow-1">
                              <span className="d-block smaller text-muted">{staticCoupon.description}</span>
                              <span className="fw-bold text-danger small">{staticCoupon.code}</span>
                            </div>
                            <button 
                              className="btn btn-sm btn-danger py-1 px-3 fw-bold" 
                              style={{ fontSize: "0.75rem" }} 
                              onClick={() => handleApplyCoupon(staticCoupon.code)}
                            >
                              Apply
                            </button>
                          </div>
                        )}
                        <div className="input-group">
                          <input
                            type="text"
                            className={`form-control border-end-0 ${errors.coupon ? "is-invalid" : ""}`}
                            placeholder="Enter coupon code"
                            value={couponCode}
                            onChange={(e) =>
                              setCouponCode(e.target.value.toUpperCase())
                            }
                            style={{
                              height: "45px",
                              background: "#f8f9fa",
                              fontSize: "0.85rem",
                            }}
                          />
                          <button
                            className="btn btn-danger px-3 fw-bold"
                            onClick={handleApplyCoupon}
                            disabled={couponLoading || !couponCode}
                          >
                            {couponLoading ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              "Apply"
                            )}
                          </button>
                        </div>
                        {errors.coupon && (
                          <small className="text-danger mt-1 d-block">
                            {errors.coupon}
                          </small>
                        )}
                      </>
                    ) : (
                      <div className="bg-success bg-opacity-10 border border-success border-dashed p-3 rounded-3 d-flex justify-content-between align-items-center">
                        <div>
                          <span className="fw-bold text-success small">
                            Code {appliedCoupon.code} applied!
                          </span>
                          <p className="m-0 smaller text-muted">
                            You saved Rs. {discount}
                          </p>
                        </div>
                        <button
                          className="btn btn-sm btn-link text-danger text-decoration-none fw-bold"
                          onClick={removeCoupon}
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingWidgets />
    </>
  );
}
